import React, { createContext, useCallback, useContext, useState } from 'react';

export interface Toast {
	id: string;
	title: string;
	description: string;
	type: 'success' | 'error' | 'info' | 'warning';
	duration?: number;
}

interface ToastContextType {
	toasts: Toast[];
	showToast: (
		title: string,
		description: string,
		type: Toast['type'],
		options?: { duration?: number },
	) => void;
	removeToast: (id: string) => void;
	clearAllToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
	const context = useContext(ToastContext);
	if (context === undefined) {
		throw new Error('useToast must be used within a ToastProvider');
	}
	return context;
};

interface ToastProviderProps {
	children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
	const [toasts, setToasts] = useState<Toast[]>([]);

	const showToast = useCallback(
		(
			title: string,
			description: string,
			type: Toast['type'],
			options?: { duration?: number },
		) => {
			const id = Math.random().toString(36).substr(2, 9);
			const duration = options?.duration || 5000;

			const newToast: Toast = {
				id,
				title,
				description,
				type,
				duration,
			};

			setToasts((prev) => [...prev, newToast]);

			// Auto remove toast after duration
			if (duration > 0) {
				setTimeout(() => {
					removeToast(id);
				}, duration);
			}
		},
		[],
	);

	const removeToast = useCallback((id: string) => {
		setToasts((prev) => prev.filter((toast) => toast.id !== id));
	}, []);

	const clearAllToasts = useCallback(() => {
		setToasts([]);
	}, []);

	const value: ToastContextType = {
		toasts,
		showToast,
		removeToast,
		clearAllToasts,
	};

	return (
		<ToastContext.Provider value={value}>
			{children}
			<ToastContainer />
		</ToastContext.Provider>
	);
};

// Simple toast container component
const ToastContainer: React.FC = () => {
	const { toasts, removeToast } = useToast();

	if (toasts.length === 0) return null;

	return (
		<div className="fixed top-4 right-4 z-50 space-y-2">
			{toasts.map((toast) => (
				<div
					key={toast.id}
					className={`
                        max-w-sm p-4 rounded-lg shadow-lg border
                        transition-all duration-300 ease-in-out
                        ${toast.type === 'success' ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900 dark:border-green-700 dark:text-green-100' : ''}
                        ${toast.type === 'error' ? 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900 dark:border-red-700 dark:text-red-100' : ''}
                        ${toast.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900 dark:border-yellow-700 dark:text-yellow-100' : ''}
                        ${toast.type === 'info' ? 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900 dark:border-blue-700 dark:text-blue-100' : ''}
                    `}
				>
					<div className="flex justify-between items-start">
						<div className="flex-1">
							<h4 className="font-medium text-sm">{toast.title}</h4>
							<p className="mt-1 text-sm opacity-90">{toast.description}</p>
						</div>
						<button
							onClick={() => removeToast(toast.id)}
							className="ml-3 flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
						>
							<span className="sr-only">Close</span>
							<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
								<path
									fillRule="evenodd"
									d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
									clipRule="evenodd"
								/>
							</svg>
						</button>
					</div>
				</div>
			))}
		</div>
	);
};
